import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import { FaFacebook, FaSync, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { IconType, IconBaseProps } from "react-icons";
import Select from "react-select";

/* ===============================
   API BASEnpm
================================ */
//export const BASE_URL = "https://rprocrmsasdevapi.rprocrm.com/api";
//export const BASE_URL = "https://metropreapi.rprocrm.com/api";
//export const BASE_URL = "http://localhost:8088/api";
export const BASE_URL = "https://zentroapi.orimark.com/api";

/* ===============================
   SAFE ICON WRAPPER (FIXES TS2786)
================================ */
type IconProps = {
  icon: IconType;
  size?: number;
  className?: string;
};

const Icon: React.FC<IconProps> = ({ icon: IconComponent, size, className }) => {
  const Comp = IconComponent as React.FC<IconBaseProps>;
  return <Comp size={size} className={className} />;
};

/* ===============================
   TYPES
================================ */
interface FacebookForm {
  formId: string;
  formName: string;
  pageName: string;
  status: string;
}

interface FormMapping {
  fbFormId: string;
  campaignId: string;
  campaignName: string;
  isActive: boolean;
  autoSync?: boolean;
}

interface CampaignApiItem {
  campaignId: string;
  campaignName: string;
}

interface CampaignOption {
  value: string;
  label: string;
}

/* ===============================
   TABLE STYLES - PREMIUM
================================ */
const tableStyles = {
  table: {
    style: {
      borderRadius: "8px",
      overflow: "hidden",
    },
  },
  headRow: {
    style: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#ffffff",
      minHeight: "48px",
      borderRadius: "8px 8px 0 0",
      borderBottom: "none",
    },
  },
  headCells: {
    style: {
      fontWeight: 700,
      fontSize: "11px",
      textTransform: "uppercase" as const,
      letterSpacing: "0.5px",
      color: "#ffffff",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      minHeight: "56px",
      fontSize: "13px",
      borderBottom: "1px solid #e2e8f0",
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: "#f8f9fa",
      },
      "&:last-child": {
        borderBottom: "none",
      },
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
      paddingTop: "12px",
      paddingBottom: "12px",
    },
  },
};

const Social: React.FC = () => {
  const location = useLocation();

  /* ===============================
     UI STATE
  =============================== */
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const toggleSidebar = () => setIsSidebarCollapsed((p) => !p);

  /* ===============================
     DATA STATE
  ================================ */
  const [facebookConnected, setFacebookConnected] = useState(false);
  const [forms, setForms] = useState<FacebookForm[]>([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);

  const [formMappings, setFormMappings] = useState<
    Record<string, { campaignId: string; campaignName: string; isActive: boolean; autoSync: boolean }>
  >({});

  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [activeFormId, setActiveFormId] = useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] =
    useState<CampaignOption | null>(null);

  /* ===============================
     FETCH CAMPAIGNS
  ================================ */
  const fetchCampaigns = async () => {
    const tenantId = localStorage.getItem("tenantId");
    const token = localStorage.getItem("authToken");
    if (!tenantId || !token) return;

    try {
      const res = await fetch(
        `${BASE_URL}/Campaign/GetAllCampaignService?tenantId=${tenantId}&isActive=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await res.json();
      const list: CampaignApiItem[] = data.campaigns ?? [];

      setCampaigns(
        list.map((c) => ({
          value: c.campaignId,
          label: c.campaignName,
        }))
      );
    } catch {
      toast.error("Failed to load campaigns");
    }
  };

  /* ===============================
     FACEBOOK STATUS
  ================================ */
  const fetchFacebookStatus = async () => {
    const tenantId = localStorage.getItem("tenantId");
    if (!tenantId) return;

    const res = await fetch(
      `${BASE_URL}/social/facebook/status?tenantId=${tenantId}`
    );
    const data = await res.json();
    setFacebookConnected(data.connected === true);
  };

  /* ===============================
     FORMS & MAPPINGS
  ================================ */
  const fetchFacebookForms = async () => {
    const tenantId = localStorage.getItem("tenantId");
    if (!tenantId) return;

    setFormsLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/social/facebook/GetFacebookForms?tenantId=${tenantId}`
      );
      setForms(await res.json());
    } catch (error) {
      toast.error("Failed to load forms");
    } finally {
      setFormsLoading(false);
    }
  };

  const fetchFormMappings = async () => {
    const tenantId = localStorage.getItem("tenantId");
    if (!tenantId) return;

    const res = await fetch(
      `${BASE_URL}/social/facebook/form-mappings?tenantId=${tenantId}`
    );
    const data: FormMapping[] = await res.json();

    const map: Record<string, { campaignId: string; campaignName: string; isActive: boolean; autoSync: boolean }> = {};
    data.forEach((m) => {
      map[m.fbFormId] = {
        campaignId: m.campaignId,
        campaignName: m.campaignName || "",
        isActive: m.isActive,
        autoSync: m.autoSync ?? false,
      };
    });

    setFormMappings(map);
  };

  useEffect(() => {
    fetchFacebookStatus();
    fetchFacebookForms();
    fetchFormMappings();
    fetchCampaigns();
  }, [location.search]);

  /* ===============================
     SYNC LEADS
  ================================ */
  const handleSyncLeads = async (formId?: string) => {
    const tenantId = localStorage.getItem("tenantId");
    if (!tenantId) return;

    setIsSyncing(true);
    const toastId = toast.loading("Syncing leads...");

    try {
      const res = await fetch(`${BASE_URL}/social/facebook/sync-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantId: Number(tenantId),
          fbFormId: formId, // if undefined, sync all mapped forms
        }),
      });

      const data = await res.json();
      
      toast.success(`Successfully synced ${data.leadsCount || 0} leads!`, {
        id: toastId,
      });
      
      setLastSyncTime(new Date().toLocaleString());
    } catch (error) {
      toast.error("Failed to sync leads", { id: toastId });
    } finally {
      setIsSyncing(false);
    }
  };

  /* ===============================
     TOGGLE AUTO SYNC
  ================================ */
  const handleToggleAutoSync = async (form: FacebookForm) => {
    const tenantId = localStorage.getItem("tenantId");
    const userId = localStorage.getItem("UserId");
    const currentAutoSync = formMappings[form.formId]?.autoSync ?? false;

    try {
      await fetch(`${BASE_URL}/social/facebook/toggle-autosync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fbFormId: form.formId,
          tenantId: Number(tenantId),
          userId: Number(userId),
          autoSync: !currentAutoSync,
        }),
      });

      setFormMappings((p) => ({
        ...p,
        [form.formId]: {
          ...p[form.formId],
          autoSync: !currentAutoSync,
        },
      }));

      toast.success(
        `Auto-sync ${!currentAutoSync ? "enabled" : "disabled"} for ${form.formName}`
      );
    } catch (error) {
      toast.error("Failed to toggle auto-sync");
    }
  };

  /* ===============================
     MAP / UNMAP
  ================================ */
  const handleSaveMap = async (form: FacebookForm) => {
    if (!selectedCampaign) {
      toast.error("Select campaign");
      return;
    }

    const tenantId = localStorage.getItem("tenantId");
    const userId = localStorage.getItem("UserId");

    await fetch(`${BASE_URL}/social/facebook/map-form`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fbFormId: form.formId,
        fbFormName: form.formName,
        mapToCampaign: selectedCampaign.value,
        tenantId: Number(tenantId),
        userId: Number(userId),
      }),
    });

    toast.success("Mapped successfully");

    setFormMappings((p) => ({
      ...p,
      [form.formId]: { 
        campaignId: selectedCampaign.value, 
        campaignName: selectedCampaign.label, 
        isActive: true, 
        autoSync: false 
      },
    }));

    setActiveFormId(null);
    setSelectedCampaign(null);
  };

  const handleUnmap = async (form: FacebookForm) => {
    const tenantId = localStorage.getItem("tenantId");
    const userId = localStorage.getItem("UserId");

    await fetch(`${BASE_URL}/social/facebook/unmap-form`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fbFormId: form.formId,
        tenantId: Number(tenantId),
        userId: Number(userId),
      }),
    });

    toast.success("Unmapped");

    setFormMappings((p) => ({
      ...p,
      [form.formId]: { campaignId: "", campaignName: "", isActive: false, autoSync: false },
    }));
  };

  /* ===============================
     TABLE COLUMNS
  ================================ */
  const columns: TableColumn<FacebookForm>[] = [
    { 
      name: "#", 
      cell: (_r, i) => (
        <div style={{ fontSize: "14px", fontWeight: 600, color: "#2d3748" }}>
          {i + 1}
        </div>
      ), 
      width: "60px",
      center: true,
    },
    { 
      name: "FORM NAME", 
      selector: (r) => r.formName, 
      cell: (r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#2d3748", marginBottom: "2px" }}>
            {r.formName}
          </div>
          <small className="text-muted" style={{ fontSize: "11px" }}>
            ID: {r.formId.substring(0, 15)}...
          </small>
        </div>
      ),
      width: "280px",
    },
    { 
      name: "PAGE NAME", 
      selector: (r) => r.pageName,
      cell: (r) => (
        <div className="d-flex align-items-center gap-2">
          <Icon icon={FaFacebook} size={14} className="text-primary" />
          <span style={{ color: "#4a5568", fontSize: "14px" }}>{r.pageName}</span>
        </div>
      ),
      width: "200px",
    },
    {
      name: "CAMPAIGN",
      cell: (row) => {
        const mapping = formMappings[row.formId];
        if (!mapping?.isActive) {
          return <span className="text-muted" style={{ fontSize: "13px" }}>—</span>;
        }
        
        // Use campaignName directly from the mapping
        const campaignName = mapping.campaignName;
        
        return campaignName ? (
          <span className="badge" style={{ 
            backgroundColor: "#e6f7ff", 
            color: "#0066cc",
            padding: "5px 12px",
            fontWeight: 500,
            fontSize: "12px",
            borderRadius: "4px",
          }}>
            {campaignName}
          </span>
        ) : (
          <span className="badge" style={{ 
            backgroundColor: "#f0f0f0", 
            color: "#666",
            padding: "5px 12px",
            fontWeight: 500,
            fontSize: "12px",
            borderRadius: "4px",
          }}>
            Unknown
          </span>
        );
      },
      width: "200px",
    },
    {
      name: "STATUS",
      cell: (row) => {
        const isMapped = formMappings[row.formId]?.isActive;
        return isMapped ? (
          <span className="badge d-inline-flex align-items-center gap-1" style={{
            backgroundColor: "#d4edda",
            color: "#155724",
            padding: "5px 10px",
            fontWeight: 500,
            fontSize: "11px",
            borderRadius: "4px",
          }}>
            <Icon icon={FaCheckCircle} size={12} />
            MAPPED
          </span>
        ) : (
          <span className="badge d-inline-flex align-items-center gap-1" style={{
            backgroundColor: "#f8d7da",
            color: "#721c24",
            padding: "5px 10px",
            fontWeight: 500,
            fontSize: "11px",
            borderRadius: "4px",
          }}>
            <Icon icon={FaTimesCircle} size={12} />
            UNMAPPED
          </span>
        );
      },
      width: "130px",
      center: true,
    },
    {
      name: "ACTIONS",
      cell: (row) => {
        const isMapped = formMappings[row.formId]?.isActive;
        const isEditing = activeFormId === row.formId;

        return (
          <div className="d-flex gap-2 align-items-center" style={{ width: "100%" }}>
            {isMapped ? (
              <>
                <button 
                  className="btn btn-sm action-btn-sync"
                  onClick={() => handleSyncLeads(row.formId)}
                  disabled={isSyncing}
                >
                  <Icon icon={FaSync} size={12} className="me-1" />
                  Sync Now
                </button>
                <button 
                  className="btn btn-sm action-btn-unmap" 
                  onClick={() => handleUnmap(row)}
                >
                  Unmap
                </button>
              </>
            ) : isEditing ? (
              <>
                <div style={{ width: 200, minWidth: 200 }}>
                  <Select
                    options={campaigns}
                    value={selectedCampaign}
                    onChange={(v) => setSelectedCampaign(v)}
                    isSearchable
                    placeholder="Select campaign"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "34px",
                        height: "34px",
                        fontSize: "13px",
                        borderColor: "#ced4da",
                        borderRadius: "6px",
                        '&:hover': {
                          borderColor: "#667eea",
                        }
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        height: "34px",
                        padding: "0 8px",
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: "34px",
                      }),
                    }}
                  />
                </div>
                <button 
                  className="btn btn-sm"
                  style={{
                    backgroundColor: "#48bb78",
                    color: "white",
                    border: "none",
                    fontWeight: 500,
                    fontSize: "13px",
                    padding: "6px 16px",
                    borderRadius: "6px",
                  }}
                  onClick={() => handleSaveMap(row)}
                >
                  Save
                </button>
                <button 
                  className="btn btn-sm" 
                  onClick={() => setActiveFormId(null)}
                  style={{ 
                    backgroundColor: "#6c757d",
                    color: "white",
                    border: "none",
                    fontWeight: 500,
                    fontSize: "13px",
                    padding: "6px 16px",
                    borderRadius: "6px",
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button 
                className="btn btn-sm action-btn-map"
                onClick={() => setActiveFormId(row.formId)}
              >
                Map to Campaign
              </button>
            )}
          </div>
        );
      },
      width: "300px",
    },
  ];

  /* ===============================
     RENDER
  ================================ */
  return (
    <div className="wrapper">
      <Toaster position="top-right" />
      {!isSidebarCollapsed && <CollapsibleSidebar />}

      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />

        <main className="content p-4">
          {/* Premium Header Card */}
          <div 
            className="card mb-4 border-0"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
            }}
          >
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3 text-white">
                  <div 
                    className="p-3 bg-white bg-opacity-20 rounded-circle"
                    style={{ backdropFilter: "blur(10px)" }}
                  >
                    <Icon icon={FaFacebook} size={32} />
                  </div>
                  <div>
                    <h4 className="mb-1 fw-bold">Facebook Lead Ads</h4>
                    <p className="mb-0 opacity-90">Automatically sync leads to your campaigns</p>
                  </div>
                  {facebookConnected && (
                    <span 
                      className="badge d-flex align-items-center gap-1"
                      style={{
                        backgroundColor: "rgba(255,255,255,0.25)",
                        color: "white",
                        padding: "8px 16px",
                        fontSize: "13px",
                        fontWeight: 600,
                      }}
                    >
                      <Icon icon={FaCheckCircle} size={14} />
                      CONNECTED
                    </span>
                  )}
                </div>

                {facebookConnected ? (
                  <button
                    className="btn btn-light btn-sm fw-semibold"
                    style={{
                      padding: "10px 24px",
                      borderRadius: "8px",
                    }}
                    onClick={async () => {
                      const tenantId = localStorage.getItem("tenantId");
                      await fetch(
                        `${BASE_URL}/social/facebook/disconnect?tenantId=${tenantId}`,
                        { method: "POST" }
                      );
                      fetchFacebookStatus();
                      toast.success("Disconnected from Facebook");
                    }}
                  >
                    Disconnect
                  </button>
                ) : (
                  <button
                    className="btn btn-light fw-bold"
                    style={{
                      padding: "12px 32px",
                      borderRadius: "8px",
                      fontSize: "15px",
                    }}
                    onClick={() =>
                      (window.location.href = `${BASE_URL}/social/facebook/connect?tenantId=${localStorage.getItem(
                        "tenantId"
                      )}&userId=${localStorage.getItem("UserId")}`)
                    }
                  >
                    <Icon icon={FaFacebook} size={18} className="me-2" />
                    Connect Facebook
                  </button>
                )}
              </div>
            </div>
          </div>

          {facebookConnected && (
            <>
              {/* Control Bar */}
              <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "12px" }}>
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-0 fw-bold" style={{ color: "#2d3748", fontSize: "15px" }}>
                        Lead Forms Management
                      </h6>
                    </div>
                    <div className="d-flex gap-2">
                      <button 
                        className="btn btn-sm d-flex align-items-center gap-2"
                        style={{
                          backgroundColor: "#667eea",
                          color: "white",
                          border: "none",
                          padding: "8px 18px",
                          fontWeight: 500,
                          fontSize: "13px",
                          borderRadius: "6px",
                        }}
                        onClick={() => handleSyncLeads()}
                        disabled={isSyncing}
                      >
                        <Icon icon={FaSync} size={13} className={isSyncing ? "spin" : ""} />
                        Sync All Leads
                      </button>
                      <button 
                        className="btn btn-sm d-flex align-items-center gap-2"
                        onClick={fetchFacebookForms}
                        style={{
                          backgroundColor: "white",
                          color: "#667eea",
                          border: "1.5px solid #667eea",
                          padding: "8px 18px",
                          fontWeight: 500,
                          fontSize: "13px",
                          borderRadius: "6px",
                        }}
                      >
                        <Icon icon={FaSync} size={13} />
                        Refresh Forms
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="card border-0 shadow-sm" style={{ 
                borderRadius: "12px", 
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}>
                <DataTable
                  columns={columns}
                  data={forms}
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 20, 30, 50]}
                  progressPending={formsLoading}
                  customStyles={tableStyles}
                  highlightOnHover
                  pointerOnHover
                  dense={false}
                  noDataComponent={
                    <div className="text-center py-5">
                      <Icon icon={FaFacebook} size={48} className="text-muted mb-3" />
                      <h6 className="text-muted">No forms found</h6>
                      <p className="text-muted small">
                        Create lead forms in Facebook Business Manager to get started
                      </p>
                    </div>
                  }
                />
              </div>
            </>
          )}
        </main>

        <Footer />
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .form-check-input:checked {
          background-color: #667eea !important;
          border-color: #667eea !important;
        }

        .form-check-input {
          cursor: pointer;
        }

        /* Action button styles - prevent color changes */
        .action-btn-sync {
          background-color: #667eea !important;
          color: white !important;
          border: none !important;
          padding: 6px 16px !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          border-radius: 6px !important;
        }

        .action-btn-sync:hover,
        .action-btn-sync:focus,
        .action-btn-sync:active {
          background-color: #667eea !important;
          color: white !important;
          border: none !important;
          box-shadow: none !important;
        }

        .action-btn-sync:disabled {
          background-color: #667eea !important;
          opacity: 0.6;
        }

        .action-btn-unmap {
          background-color: white !important;
          color: #dc3545 !important;
          border: 1px solid #dc3545 !important;
          padding: 6px 16px !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          border-radius: 6px !important;
        }

        .action-btn-unmap:hover,
        .action-btn-unmap:focus,
        .action-btn-unmap:active {
          background-color: white !important;
          color: #dc3545 !important;
          border: 1px solid #dc3545 !important;
          box-shadow: none !important;
        }

        .action-btn-map {
          background-color: white !important;
          color: #667eea !important;
          border: 1.5px solid #667eea !important;
          padding: 6px 18px !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          border-radius: 6px !important;
        }

        .action-btn-map:hover,
        .action-btn-map:focus,
        .action-btn-map:active {
          background-color: white !important;
          color: #667eea !important;
          border: 1.5px solid #667eea !important;
          box-shadow: none !important;
        }

        .btn:hover {
          transform: none !important;
          box-shadow: none !important;
        }

        .btn:active {
          transform: none !important;
        }

        .card {
          transition: all 0.3s ease;
        }

        /* Table pagination styling */
        .rdt_Pagination {
          border-top: 1px solid #e2e8f0;
          padding: 12px 16px;
          background-color: #fafafa;
        }

        /* Ensure proper column alignment */
        [data-column-id] {
          display: flex;
          align-items: center;
        }

        /* Badge improvements */
        .badge {
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        /* Select dropdown improvements */
        .css-1s2u09g-control,
        .css-1pahdxg-control {
          border-radius: 6px !important;
        }

        /* Remove default focus outline, add custom */
        .form-check-input:focus {
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25) !important;
          border-color: #667eea !important;
        }
      `}</style>
    </div>
  );
};

export default Social;